import type { Deposit } from "../api/deposits";
import { createSearchMatcher } from "../utils/search";

// Търсенето на депозит — изборът в „Закрий" → „Плащане с депозит" и Справки →
// „Депозити". Освен текста (име, описание…) гледа и двата номера, които
// операторът има пред себе си: номера на депозита (`id`, „Депозит № 17") и
// номера на касовия му бон (Бон 1, „576"). Номерът съвпада ЦЯЛ, не като
// начало: Enter в избора взима първия ред, а „17" не бива да даде депозит 170.

type DepositNumbers = Pick<Deposit, "id" | "receiptNumber">;

// „17", „№17", „№ 17", „No 17", „No. 17", „N° 17", „Но 17" (българска
// подредба), „#17" — знакът пред номера не е част от него.
const NUMBER_QUERY = /^(?:№|n[o°]?\.?|н[о°]?\.?|#)?\s*(\d+)$/iu;

const withoutLeadingZeros = (number: string) => number.replace(/^0+(?=.)/, "");

/** Номерът в заявката (без водещите нули) или null, ако заявката не е номер. */
export function parseDepositNumberQuery(term: string): string | null {
  const match = NUMBER_QUERY.exec(term.trim());
  return match ? withoutLeadingZeros(match[1]) : null;
}

const isSameNumber = (deposit: DepositNumbers, number: string): boolean => {
  if (String(deposit.id) === number) return true;
  const receiptNumber = deposit.receiptNumber?.trim();
  // Касата може да върне номера с водещи нули („0000576") — без значение.
  return !!receiptNumber && withoutLeadingZeros(receiptNumber) === number;
};

/**
 * Депозитите, които отговарят на заявката: по номер (на депозита или на бона)
 * ИЛИ по текста от `textsOf` (толерантно, като `createSearchMatcher`).
 * Съвпаденията по номер са най-отгоре; иначе редът на списъка се пази.
 */
export function searchDeposits<T extends DepositNumbers>(
  deposits: readonly T[],
  term: string,
  textsOf: (deposit: T) => (string | null | undefined)[],
): T[] {
  const matcher = createSearchMatcher(term);
  if (matcher.isEmpty) return [...deposits];
  const number = parseDepositNumberQuery(term);
  const byNumber: T[] = [];
  const byText: T[] = [];
  for (const deposit of deposits) {
    if (number !== null && isSameNumber(deposit, number)) byNumber.push(deposit);
    else if (matcher.matches(textsOf(deposit).filter(Boolean).join(" "))) byText.push(deposit);
  }
  return [...byNumber, ...byText];
}
